import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { action, API, controller } from "api";
// Straight from the leaf module, NOT the "hooks" barrel: DropLi, Model,
// useFormatPrice, useLogout and useReminderCounts all import "context", so
// pulling the barrel in from here closes an import cycle
// (context/index → RegisterContext → hooks/index → useFormatPrice → context/index)
// and the app dies at boot with "Cannot access 'useFormatNumber' before
// initialization". utils/localStorage imports nothing, so it is always safe.
import { lsDelete, lsGet, lsSave } from "../utils/localStorage";
import { useAppContext } from "./AppContext";

// The single source of truth for "which till may I work on right now".
//
// ---------------------------------------------------------------------------
// THE INVARIANT
//
//   A successful server answer of "none" or "blocked" is NEVER upgraded by the
//   cached blob. State transitions come from HTTP 200 bodies only. The cache is
//   written FROM state; it is never read INTO state.
//
// The one read of `localStorage.activeReg` below produces a query PARAMETER —
// the id of the till we believe we are standing at — never a decision. The
// server validates it through the same AccessService.ResolveTillSessionAsync
// that every write path runs, so what this screen shows and what the API will
// accept cannot drift apart.
// ---------------------------------------------------------------------------
//
// What this replaces: register state used to live only in `localStorage.activeReg`,
// re-read synchronously by ~50 screens and, in practice, never cleared. App.jsx
// guarded its refresh on `res.status === 200`, but the backend answered "you have
// no open session" as an Ok()-wrapped body of `{ status: 404 }` — so the clear
// branch was unreachable and a session closed by a manager stayed "open" on the
// cashier's screen forever.
//
// Why the cached id must still be sent: SellReg/GetCashierOpenReg only ever
// returned a session the caller OPENED. A cashier legitimately standing at a
// colleague's till got nothing back, which is why a stale copy had to be trusted
// in the first place. ResolveActiveReg answers for that case too.

const RegisterContext = createContext({});

export const useRegister = () => useContext(RegisterContext);

// status === 2 is Closed (EnumLi.RegStatus: Open = 1, Closed = 2, Pause = 3).
const isRegOpen = (reg) => !!(reg && reg.id && reg.status !== 2);

// A focus event fires on every alt-tab. Re-asking more often than this buys
// nothing — the question is "did a manager close my till", not a live feed.
const MIN_INTERVAL_MS = 15000;

export const RegisterProvider = ({ children }) => {
  const { authUser, selCompany } = useAppContext();

  const [state, setState] = useState("loading"); // loading | ok | none | blocked
  const [register, setRegister] = useState(null);
  const [notice, setNotice] = useState(null);

  // Refs, not state: these drive the throttle and must not re-create `resolve`.
  const inFlightRef = useRef(false);
  const lastOkAtRef = useRef(0);
  const registerRef = useRef(null);
  const prevUserRef = useRef(undefined);

  registerRef.current = register;

  const resolve = useCallback(
    (force = false) => {
      // Stay in "loading" until we have both halves of the question. selCompany
      // is resolved later by BizContext, so this legitimately fires twice.
      if (!authUser?.id || !selCompany) return;
      if (inFlightRef.current) return;
      if (!force && Date.now() - lastOkAtRef.current < MIN_INTERVAL_MS) return;

      // The cached id survives a hard reload, where component state does not —
      // so on a cold start it is the only record of a colleague's till we were
      // switched onto. A hint to validate, never an answer.
      const hintId = registerRef.current?.id || lsGet("activeReg")?.id || 0;

      inFlightRef.current = true;
      API(
        controller.SellReg + action.ResolveActiveReg + `/${selCompany}/${hintId}`
      ).get((res) => {
        inFlightRef.current = false;

        // Transport failure, 401, 403, 500 — we learned NOTHING. Hold the last
        // known state and leave the cache alone: a flaky connection must not
        // read as "your register was closed" and lock a till out of trading.
        // Only a successful answer moves the state, in either direction.
        if (res?.status !== 200 || !res.result) return;

        lastOkAtRef.current = Date.now();
        const { state: next, register: reg, notice: why } = res.result;

        if (next === "ok" || next === "blocked") {
          // "blocked" is still cached deliberately: it is a real open session in
          // this tenant, and Stock Wastage records against a till a colleague
          // opened. Blocking is a SELLING rule, not an existence rule — POS is
          // gated on `state`, not on the presence of the key.
          lsSave("activeReg", { ...reg });
          setRegister(reg);
        } else {
          lsDelete("activeReg");
          setRegister(null);
        }

        setNotice(why || null);
        setState(next);
      });
    },
    [authUser?.id, selCompany]
  );

  // Identity or company changed: drop everything first so no screen renders the
  // previous user's till for even one frame, then re-ask.
  useEffect(() => {
    // A genuine switch of PERSON — logout/login on the same terminal, or the
    // Switch User dialog, which is a third identity change nothing used to clear.
    // The previous cashier's till is not a hint for the new one, so drop the
    // cached id too and let them start from "what do I have open".
    //
    // Deliberately NOT on the first resolve (prev is undefined → a page reload,
    // not a switch): there the cached id is the only record of a colleague's till
    // we were switched onto, and throwing it away would break that flow.
    const prev = prevUserRef.current;
    if (prev && authUser?.id && prev !== authUser.id) lsDelete("activeReg");
    prevUserRef.current = authUser?.id;

    setState("loading");
    setRegister(null);
    setNotice(null);
    // The ref too, and BEFORE resolve: setRegister only lands on the next
    // render, so leaving it would send the previous user's till id as the hint
    // we just deleted from the cache.
    registerRef.current = null;
    lastOkAtRef.current = 0;
    resolve(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolve, authUser?.id]);

  // Re-ask whenever the operator comes back to this window, when another tab in
  // the same profile changes the till, and when the network returns. Before this
  // the app had no focus, visibility, storage or online listener anywhere — a tab
  // left open showed its state as of the last mount, indefinitely.
  useEffect(() => {
    if (!authUser?.id || !selCompany) return;

    const onFocus = () => resolve(false);
    const onVisible = () => {
      if (document.visibilityState === "visible") resolve(false);
    };
    // key === null is a localStorage.clear() (the CACHE_VERSION wipe).
    const onStorage = (e) => {
      if (e.key === "activeReg" || e.key === null) resolve(true);
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("storage", onStorage);
    window.addEventListener("online", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("online", onFocus);
    };
  }, [resolve, authUser?.id, selCompany]);

  // Called by the open / switch / close flows instead of writing the cache
  // themselves. SwitchReg used to write an arbitrary register into localStorage
  // with no server call at all. Forced — the state just changed by our own hand.
  const refresh = useCallback(() => resolve(true), [resolve]);

  // For screens that want a fresh answer when the operator ARRIVES on them.
  // Navigating from the dashboard into POS fires no focus event, so without this
  // a till closed while the cashier sat on another screen would not surface until
  // they alt-tabbed. Throttled, so walking between screens costs nothing.
  const revalidate = useCallback(() => resolve(false), [resolve]);

  // Logout and switch-user. The provider stays mounted across both.
  const clearRegister = useCallback(() => {
    lsDelete("activeReg");
    setRegister(null);
    setNotice(null);
    setState("loading");
    lastOkAtRef.current = 0;
  }, []);

  return (
    <RegisterContext.Provider
      value={{
        state,
        register,
        notice,
        isOpen: state === "ok" && isRegOpen(register),
        refresh,
        revalidate,
        clearRegister,
      }}
    >
      {children}
    </RegisterContext.Provider>
  );
};

export { RegisterContext, isRegOpen };
